import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import ReportBug from "../../components/header/components/ReportBug";
import IconChevronDown from "../../components/icons/IconChevronDown";
import IconChevronRight from "../../components/icons/IconChevronRight";
import IconDoubleChevronLeft from "../../components/icons/IconDoubleChevronLeft";
import { PUBLIC_ASSET_BASE } from "../../constants/env";
import { useAuth } from "../../contexts/AuthContext";
import { BreadcrumbItem, useBreadcrumbs } from "../../store/BreadcrumbContext";
import { usePermissions } from "../../store/PermissionContext";
import { navigateToVueRoute } from "../../utils/navigationHandler";
import type { MenuItem } from "./types";
import { useActiveMenu } from "./useActiveMenu";
import { useFilteredMenu } from "./useFilteredMenu";

const RAIL_ICON_SIZE_PX = 22;
const PANEL_ICON_SIZE_PX = 18;
const CHEVRON_SIZE_PX = 14;

/*
  AsideMenu styling — mirrors RW-ERP-Service `nav.scss`.

  CSS custom properties drive every themable color. Defaults are the Vue
  dark-theme palette; a `[data-theme="light"]` override block on the
  root swaps the panel-side tokens (rail stays dark green in both modes).

  Rail bg:           #031C03  (--rail-bg)
  Rail active bg:    #103a16  (--rail-active-bg)
  Rail active edge:  #FFFFFF  (4 px white left border, --rail-active-edge)
  Logo bg:           #E5E1D8  (--logo-bg)
  Panel bg:          #1E1E1E (dark) / #FFFFFF (light)  (--panel-bg)
  Active accent:     #0EAA6B  (--panel-active)
  Active parent bg:  #212121 (dark) / #F8FCFB (light)  (--panel-active-bg)
  Guide line:        #373737 (dark) / rgba(15,23,42,.12) (light)  (--panel-guide)
*/

// Theme tokens — both dark defaults AND light-theme overrides live in a
// single inline <style> block scoped to `.aside-menu-root`. Previously
// the dark tokens were set via inline `style={...}` and the light block
// in the <style> tag, but inline styles win over CSS rule specificity
// (inline > selectors, regardless of selector specificity), so the
// light-theme override never took effect and the panel stayed dark even
// after the user flipped to light mode. Moving both layers into the
// same <style> block keeps them subject to the same specificity rules:
// `[data-theme="light"] .aside-menu-root` naturally wins when the
// `data-theme` attribute is `light`.
const ASIDE_MENU_THEME_STYLE = `
.aside-menu-root {
  --rail-bg: #031c03;
  --rail-active-bg: #103a16;
  --rail-active-edge: #ffffff;
  --rail-edge-width: 4px;
  --rail-icon-color: #ffffff;
  --rail-label-color: #ffffff;
  --rail-width: 83px;
  --logo-bg: #e5e1d8;
  --logo-text-color: #1a4d2e;
  --panel-bg: #1e1e1e;
  --panel-text: rgba(255, 255, 255, 0.7);
  --panel-text-strong: #ffffff;
  --panel-active: #0eaa6b;
  --panel-active-bg: #212121;
  --panel-guide: #373737;
  --panel-collapse-color: #ebebeb;
  --panel-empty-text: rgba(255, 255, 255, 0.5);
  --panel-rail-divider: #3f3f44;
  --panel-width: 220px;
}
[data-theme="light"] .aside-menu-root {
  --panel-bg: #ffffff;
  --panel-text: #4b5563;
  --panel-text-strong: #1f2937;
  /* Was #ecfdf5 (Tailwind's emerald-50) — a noticeably saturated mint
     green next to Vue's actual --hk-date-range-background-color-light
     (base.scss:503), which is a near-white #f8fcfb. Dark-theme value
     (#212121) already matched; only light mode was off. */
  --panel-active-bg: #f8fcfb;
  --panel-guide: rgba(15, 23, 42, 0.12);
  --panel-collapse-color: #1f2937;
  --panel-empty-text: rgba(15, 23, 42, 0.45);
  --panel-rail-divider: rgba(15, 23, 42, 0.08);
}
/*
  Rail scroll area — scrollbar hidden by request: the rail is only 83 px
  wide, and a 6 px gutter inside it is a visible chunk of the column.
  Wheel, trackpad and keyboard scrolling all still work; tabbing to a
  button below the fold scrolls it into view natively.

  These three declarations live here rather than as Tailwind arbitrary
  utilities on purpose. \`-ms-overflow-style\` starts with a dash, which
  Tailwind v3 reads as a negative-value prefix, and this file has been
  bitten repeatedly by arbitrary-value ambiguity around CSS variables
  (see the border / color notes further down). A plain rule in the style
  block this component already ships has no such failure mode.
*/
.aside-menu-root .aside-rail__scroll {
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.aside-menu-root .aside-rail__scroll::-webkit-scrollbar {
  display: none;
}
`;

// ── Tailwind class bundles ────────────────────────────────────────────────

// Root — `flex h-screen` mirrors Vue ERP's `.aside-bar` (`@include
// flex-row`): the rail stays a fixed-width VERTICAL left sidebar, never
// flipping to a column.
//
// Desktop (>=769px): a static in-flow sidebar, always visible.
// Mobile  (<=768px): a slide-in OVERLAY drawer — `fixed left-0 top-0
//   z-[9999]` so it floats over the content (Vue's `.app-layout-menu-
//   slider { position:absolute; z-index:9999; left:0 }`). Hidden by
//   default via `-translate-x-full`; the header hamburger toggles it
//   open (`translate-x-0`). The `max-[768px]:` prefix scopes every
//   drawer rule to mobile, so desktop layout is untouched.
const ROOT_CLASS_BASE =
  "aside-menu-root flex h-screen flex-none" +
  " max-[768px]:fixed max-[768px]:left-0 max-[768px]:top-0 max-[768px]:z-[9999]" +
  " max-[768px]:transition-transform max-[768px]:duration-[220ms] max-[768px]:ease-linear";
const ROOT_CLASS_OPEN = " max-[768px]:translate-x-0";
const ROOT_CLASS_CLOSED = " max-[768px]:-translate-x-full";

// Rail (left sidebar) — width fed by `--rail-width`, dark green
// background. Stays a fixed-width vertical column at all viewport sizes
// (Vue parity — see ROOT_CLASS_BASE note). The `border-r` runs the full
// height, never flipping to a bottom border on mobile.
// `overflow-hidden` keeps the rail's own box the hard boundary: the nav
// inside it is the only thing that scrolls, so a long menu can never
// paint past the rail and get clipped by AppLayout's `overflow-hidden`
// instead — which is what used to swallow the Logout row whole.
const RAIL_CLASS =
  "w-[var(--rail-width)] flex-none flex flex-col relative overflow-hidden" +
  " bg-[var(--rail-bg)] text-[var(--rail-label-color)]" +
  " border-r border-solid border-[var(--panel-rail-divider)]";

// `flex-none` pins the logo's 77 px. Without it the logo is a shrinkable
// flex item, so a menu taller than the viewport squeezes the logo instead
// of scrolling — the brand block silently shortens as items are added.
const RAIL_LOGO_CLASS =
  "h-[77px] flex-none bg-[var(--logo-bg)] flex flex-col items-center justify-center gap-[2px]";

// The scroll container. `flex-1` claims the height left over after the
// logo; `min-h-0` is what actually lets it shrink below its content —
// a column flex item defaults to `min-height: auto`, which would keep
// the nav at full content height and leave nothing to scroll.
//
// Scrolling lives HERE rather than on the rail so the logo stays put
// while the items move under it.
const RAIL_NAV_CLASS = "aside-rail__scroll flex-1 min-h-0 overflow-y-auto";

// Logout is the last <li> of this same list, so it flows directly after
// the final menu item and scrolls with it. Deliberately NOT pinned to the
// bottom of the rail: with only two or three menu items a pinned Logout
// would float alone at the foot of an empty column.
const RAIL_LIST_CLASS =
  "list-none m-0 p-0 flex-1 flex flex-col";

const RAIL_ITEM_CLASS = "relative";

// Resting rail button — invisible left border so `--active` can colour it
// without shifting layout. Hover bumps the icon green via the [&_.aside-rail__icon]
// arbitrary selector.
// `text-[12px]` matches Vue ERP's `.nv-itm` which inherits the
// `@include font-style` mixin (`font-size: 12px`) — see
// RW-ERP-Service/src/styles/common/mixins.scss:35-39.
const RAIL_BTN_BASE =
  "w-full bg-transparent border-0 border-l-[var(--rail-edge-width)]" +
  " border-l-transparent border-solid text-inherit cursor-pointer" +
  " flex flex-col items-center gap-[6px] py-4 px-1 text-[12px] leading-5 font-hk-medium relative" +
  " transition-[background-color,color,border-left-color] duration-[120ms] ease-linear" +
  " hover:bg-[var(--rail-active-bg)] [&:hover_.aside-rail__icon]:text-[var(--panel-active)]" +
  " focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--panel-active)] focus-visible:outline-offset-[-3px]";

// Active rail button — white left edge, darker green pill background, plus
// a right-pointing triangle (`::after`) that visually points into the panel.
// The triangle uses the panel background color so it matches whatever theme
// the panel is on. The rail stays vertical at all sizes (Vue parity), so
// the left edge + triangle are kept on mobile too — no horizontal flip.
//
// White edge implementation:
//   The previous version used `!border-l-[var(--rail-active-edge)]`. That
//   Tailwind shorthand is AMBIGUOUS when the value is a CSS variable —
//   the compiler can't tell whether to emit `border-left-color` or
//   `border-left-width`, so the white edge sometimes failed to paint.
//   On top of that, `RAIL_BTN_BASE` declares both `border-0` (width: 0
//   all sides) and `border-l-[var(--rail-edge-width)]` (width: 4 px
//   left), and their compile-order race could zero out the 4 px slot
//   entirely.
//
//   We now paint the white edge with `box-shadow: inset 4px 0 0 …`
//   (Tailwind: `!shadow-[inset_4px_0_0_var(--rail-active-edge)]`). The
//   inset shadow lays a 4 px solid stripe along the inside left edge
//   of the tile, with NO dependency on the border cascade and NO
//   layout impact. Same pattern as the panel active-bar fix. Mirrors
//   Vue ERP `.show-border` (nav.scss:41-44) — `border-left: 4px solid
//   var(--vt-c-white) !important;`.
//
//   `!important` on bg + shadow so the active state always wins over
//   the resting RAIL_BTN_BASE.
const RAIL_BTN_ACTIVE =
  " !bg-[var(--rail-active-bg)] !shadow-[inset_4px_0_0_var(--rail-active-edge)]" +
  " text-[var(--rail-label-color)] font-hk-semibold" +
  " after:content-[''] after:absolute after:right-[-8px] after:top-1/2" +
  " after:-translate-y-1/2 after:w-0 after:h-0" +
  " after:border-solid after:[border-width:8px_8px_8px_0]" +
  " after:[border-color:transparent_var(--panel-bg)_transparent_transparent]" +
  " after:pointer-events-none";

const RAIL_BTN_LOGOUT_EXTRA = " font-hk-medium mt-1";

const RAIL_ICON_CLASS = "aside-rail__icon text-[var(--rail-icon-color)]";
const RAIL_LABEL_CLASS = "text-center";

// Panel (right column) — width fed by `--panel-width`, slim themed
// scrollbar. Webkit scrollbar gets thumb+track via arbitrary selectors.
const PANEL_CLASS =
  "w-[var(--panel-width)] flex-none flex flex-col h-screen overflow-y-auto" +
  " bg-[var(--panel-bg)] text-[var(--panel-text)]" +
  " [scrollbar-width:thin] [scrollbar-color:var(--panel-guide)_transparent]" +
  " [&::-webkit-scrollbar]:w-[6px]" +
  " [&::-webkit-scrollbar-track]:bg-transparent" +
  " [&::-webkit-scrollbar-thumb]:bg-[var(--panel-guide)] [&::-webkit-scrollbar-thumb]:rounded-[3px]" +
  " [&::-webkit-scrollbar-thumb:hover]:bg-[var(--panel-text)]";

const PANEL_TOPBAR_CLASS = "flex justify-end pr-[10px] mt-5";

const PANEL_COLLAPSE_CLASS =
  "bg-transparent border-0 text-[var(--panel-collapse-color)] cursor-pointer p-1 rounded" +
  " transition-[background-color,color] duration-[120ms] ease-linear" +
  " hover:text-[var(--panel-text-strong)] hover:bg-[var(--panel-active-bg)]";

const PANEL_LIST_CLASS = "list-none m-0 p-0";

// Outer sublist (children of a top-level menu item) — provides the single
// vertical guide line via a `::before` pseudo-element at column +12.
// Position: relative + padding-left:24px so the line + dashes can be
// absolutely positioned relative to this container.
// `top-0 bottom-0` (was `top-1 bottom-1`) so the line spans the full
// sublist height edge-to-edge — the previous 4 px inset on each end
// created a visible break, especially when a nested sublist (CW's
// children) opened inside.
const SUBLIST_OUTER_CLASS =
  "list-none m-0 p-0 pl-6 relative" +
  " before:content-[''] before:absolute before:left-7 before:top-0 before:bottom-0" +
  " before:w-px before:bg-[var(--panel-guide)]";

// Nested sublist (grand-children) — no own vertical line; the outer line
// above serves the whole tree. Extra indent so children sit visibly under
// their parent. Top / bottom margins kept very small (mt-0.5 / mb-0.5 =
// 2 px each) so the outer vertical line doesn't visibly break between
// the parent row (CW) and its sub-children (CW Direct / DropShip / Stock).
const SUBLIST_NESTED_CLASS = "list-none m-0 p-0 pl-[1.6rem] mt-0.5 mb-0.5";

const PANEL_ITEM_CLASS = "relative";

// ── Row classes by menu depth ─────────────────────────────────────────────
// The panel has three distinct visual levels, mirroring Vue ERP's
// `nav.scss` hierarchy (`.product-specifications` for parents,
// `.menu-childs .product-specifications` for children, `.inner-child`
// for sub-children):
//
//   • PARENT    (depth 0) — e.g. "Sales orders". Uses ROW_BASE alone.
//   • CHILD     (depth 1) — e.g. "All orders", "Amazon", "Baldor".
//                Uses ROW_BASE + ROW_CHILD_CLASS.
//   • SUB-CHILD (depth ≥ 2) — e.g. "Amazon Canada", "CW Direct".
//                Uses ROW_BASE + ROW_SUBCHILD_CLASS (+ active connector
//                colour when applicable).
//
// Each level has a dedicated constant so the per-depth padding,
// typography, and decoration rules can be tuned independently without
// regression risk across the others.

// ── PARENT (depth 0) ──────────────────────────────────────────────────────
// Top-level menu entries — carry the section icon, label, and optional
// chevron. Reserve a 4 px transparent left border so the active green
// bar can paint in-place without shifting layout. `py-[8px]` gives a
// ~32 px tall row, comfortable for touch yet compact enough to fit the
// whole tree without scrolling.
//
// Label x-position from panel-left:
//   4 (border) + 16 (px-4) + 18 (icon) + 12 (gap) = 50 px
const ROW_BASE =
  "flex items-center gap-[12px] w-full bg-transparent border-0" +
  " border-l-2 border-l-transparent border-solid" +
  " text-[var(--panel-text)] text-left py-[8px] px-4 text-sm font-hk-semibold cursor-pointer relative" +
  " transition-[background-color,color,border-left-color] duration-[120ms] ease-linear" +
  " hover:text-[var(--panel-active)]" +
  " [&:hover_.aside-panel__icon]:text-[var(--panel-active)]" +
  " [&:hover_.aside-panel__chevron]:text-[var(--panel-active)]" +
  " focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--panel-active)] focus-visible:outline-offset-[-3px]";

// Active leaf at any depth — green text + icon.
// Uses the explicit arbitrary-PROPERTY syntax `[color:var(...)]`
// rather than the shorthand `text-[var(...)]`. The shorthand is
// AMBIGUOUS for Tailwind v3 when the value is a CSS variable (no
// `#` hex prefix): the compiler can't decide whether to emit
// `color: var(...)` or `font-size: var(...)`, and often picks the
// wrong one, silently dropping the color override and leaving the
// row painted in the inherited muted color. The explicit form maps
// directly to `color: var(--panel-active) !important`, matching the
// Vue ERP pattern in `nav.scss`:
//   .product-specifications { color: var(--hk-base-text-primary-active); }
const ROW_ACTIVE_GENERIC =
  " ![color:var(--panel-active)]" +
  " [&_.aside-panel__icon]:![color:var(--panel-active)]" +
  " [&_.aside-panel__chevron]:![color:var(--panel-active)]";

// Parent-active is structurally the same as active.
const ROW_PARENT_ACTIVE_GENERIC = ROW_ACTIVE_GENERIC;

// Top-level active row gets a 4-px brand-green LEFT BAR (matches the
// latest design — Image #9: "Sales orders" header carries the green
// vertical bar both when collapsed and when expanded). The bar slots
// into the 4-px transparent left border ROW_BASE already reserves, so
// it paints in-place with zero layout shift.
//
// Uses the explicit arbitrary-PROPERTY syntax
// `[border-left-color:var(--panel-active)]` instead of the shorthand
// `border-l-[var(--panel-active)]`. The shorthand is ambiguous for
// Tailwind v3 when the value is a CSS variable (no `#`, no `px`):
// it compiles to `border-left-width: var(--panel-active)` which is
// invalid and silently drops, leaving the border transparent. The
// explicit form maps directly to the right CSS property. Same
// disambiguation pattern used elsewhere in this codebase where
// arbitrary CSS-var values touch border colors.
const ROW_ACTIVE_TOPLEVEL_EXTRA = " ![border-left-color:var(--panel-active)]";

// Disabled-pointer flag (mirrors Vue's `.disabled-link`) — keeps the active
// leaf non-clickable so the user can't re-navigate to the page they're on.
const ROW_DISABLED_CLASS = " pointer-events-none cursor-default";

// ── CHILD (depth 1) ───────────────────────────────────────────────────────
// Direct children of a parent — e.g. "All orders", "Admin", "Amazon",
// "Baldor", "CW". No icon (icons are reserved for parents). Smaller,
// bolder type than the parent. When this row is the active leaf OR
// is a collapsed parent containing the active descendant, a green
// bullet dot (PANEL_DOT_CLASS) renders inline, right before the label
// (see PANEL_DOT_CLASS for why it's a flex child, not absolutely
// positioned).
//
// Padding:
//   • Vertical: `py-[4px]` → 4 px top + 4 px bottom (per design spec).
//     Combined with `!leading-[1.5rem]` (24 px) the row stands 32 px
//     tall, the touch-target height the design uses.
//   • Horizontal: `pl-[24px]` → with the SUBLIST_OUTER `pl-6` (24 px)
//     above it, the label lands at panel + 24 + 24 = 48 px from the
//     panel-left edge — roughly aligned with the parent label at 50 px.
//   • `pr-4` (16 px) on the right keeps a clean gutter before the
//     chevron / scrollbar.
//
// Type rules:
//   • `!text-[13px]` uses Tailwind's `!important` prefix because
//     ROW_BASE already declares `text-sm` (14 px) and a compile-order
//     tie can let the parent's `text-sm` win.
//   • `!leading-[1.5rem]` (24 px) is REQUIRED alongside `!text-[13px]`
//     — Tailwind's arbitrary `text-[13px]` sets font-size only, NOT
//     line-height. Without this, child rows inherit ROW_BASE's
//     `text-sm` line-height (20 px) and the row sizing drifts off the
//     design's vertical rhythm.
//   • `border-l-0` cancels ROW_BASE's reserved 4 px transparent border
//     — children don't get a green left bar (that's parent-only).
const ROW_CHILD_CLASS =
  " !text-[13px] !leading-[1.5rem] font-hk-bold pt-[4px] pb-[4px] pr-4 pl-[24px]" +
  " border-l-0 text-[var(--panel-text)]";

// ── SUB-CHILD (depth ≥ 2) ─────────────────────────────────────────────────
// Grand-children — e.g. "Amazon Canada", "Amazon DF", "CW Direct",
// "GFS Canada". Same typography as CHILD but with two differences:
//   1. A slightly larger left padding than child (pl-[6px] vs. pl-[4px])
//      so the text starts just past the dash connector. The deeper
//      indent comes naturally from being inside an extra nested ul
//      (SUBLIST_NESTED pl-6 = 24 px), so we don't need to over-pad
//      here.
//   2. A horizontal dash from the vertical guide line over to the row
//      (see ROW_SUBCHILD_CONNECTOR below).
//
// Label x-position from panel-left:
//   panel + 24 (outer pl-6) + 24 (nested pl-6) + 6 (this pl-[6px])
//   = 54 px → text sits right after the 24 px dash that ends at
//   panel + 48 px, leaving a 6 px gap between dash-end and text-start.
const ROW_SUBCHILD_CLASS =
  " !text-[13px] !leading-[1.5rem] font-hk-bold pt-[4px] pb-[4px] pr-4 pl-[6px]" +
  " border-l-0 text-[var(--panel-text)]";

// Horizontal dash connector for sub-child rows — draws a 24 px wide,
// 1 px tall line from the vertical guide line over to the row's left
// edge, forming a clean L-junction.
// Geometry:
//   • Sub-child button-left sits at panel + 48 px.
//   • `before:left-[-24px]` → dash starts 24 px left of the button,
//     i.e. panel + 24 px.
//   • `before:w-6` → dash is 24 px wide → ends at the button-left
//     (panel + 48 px).
//   • The vertical guide line sits at panel + 28 px, so the dash
//     overlaps the line by 4 px on the inside — eliminating any
//     sub-pixel / anti-aliasing hairline gap at the junction.
const ROW_SUBCHILD_CONNECTOR =
  " before:content-[''] before:absolute before:left-[-24px] before:top-1/2" +
  " before:w-6 before:h-px before:bg-[var(--panel-guide)]";

// Active variant of the sub-child dash — paints the dash brand-green
// so the active sub-child (e.g. "Amazon Canada") gets a matching
// green dash beside its green text, per the design.
//
// Uses the explicit `[background-color:var(--panel-active)]` arbitrary-
// PROPERTY syntax instead of the shorthand `bg-[var(--panel-active)]`
// to dodge Tailwind v3's CSS-variable shorthand ambiguity. `!important`
// defeats compile-order ties with the muted `before:bg-[var(--panel-guide)]`
// declaration on ROW_SUBCHILD_CONNECTOR.
const ROW_SUBCHILD_CONNECTOR_ACTIVE =
  " before:![background-color:var(--panel-active)]";

// Sublist wrapper — `grid-template-rows: 0fr → 1fr` trick for animated
// open/close without measuring height in JS.
const SUBLIST_WRAPPER_BASE =
  "grid grid-rows-[0fr] opacity-0 transition-[grid-template-rows,opacity] duration-[220ms] ease-linear" +
  " [&>ul]:min-h-0 [&>ul]:overflow-hidden";
const SUBLIST_WRAPPER_OPEN = " grid-rows-[1fr] opacity-100";

// Logo
const LOGO_IMG_CLASS = "w-[42px] h-[52px] object-contain";

// Aside panel icon / chevron / label (used inside ROW). These class names
// are kept for the `[&_.foo]:` selectors above; if they ever stop being
// hardcoded the hover-greening will silently break.
const PANEL_ICON_CLASS = "aside-panel__icon text-current shrink-0";
const PANEL_CHEVRON_CLASS =
  "aside-panel__chevron text-current opacity-70 shrink-0";

// Compose the `<button>` row className for a panel menu item. Extracted from
// `renderPanelItem` to reduce its cognitive complexity (S3776).
//
// The three depth tiers route to distinct row classes:
//   • depth === 0  → ROW_BASE (parent)
//   • depth === 1  → ROW_BASE + ROW_CHILD_CLASS
//   • depth >= 2   → ROW_BASE + ROW_SUBCHILD_CLASS + ROW_SUBCHILD_CONNECTOR
//
// Active / parent-highlighted states layer on top of the depth class.
function computePanelRowClassName(args: {
  depth: number;
  isActiveLeaf: boolean;
  isUrlActiveLeaf: boolean;
  isParentHighlighted: boolean;
  hasChildren: boolean;
}): string {
  const { depth, isActiveLeaf, isUrlActiveLeaf, isParentHighlighted, hasChildren } =
    args;
  let className = ROW_BASE;
  if (depth === 1) {
    className += ROW_CHILD_CLASS;
  } else if (depth >= 2) {
    className += ROW_SUBCHILD_CLASS + ROW_SUBCHILD_CONNECTOR;
  }
  if (isActiveLeaf) {
    className += ROW_ACTIVE_GENERIC;
    if (depth === 0) className += ROW_ACTIVE_TOPLEVEL_EXTRA;
    // Active sub-child (depth ≥ 2) paints its horizontal connector
    // dash in brand green to match the green leaf text.
    if (depth >= 2) className += ROW_SUBCHILD_CONNECTOR_ACTIVE;
    // Disable re-navigation only for a GENUINE URL match on a true leaf.
    // Two reasons this is narrower than plain `isActiveLeaf`:
    //  - A click-memory-only match (new-tab Vue navigation; this tab's URL
    //    never changed) isn't really "the page you're on" — disabling it
    //    would strand the row unclickable with nothing to show for it.
    //  - A group parent (GFS, Amazon, Sales orders, …) can be BOTH the
    //    active leaf (its own route is the current page) AND still need
    //    its expand/collapse chevron to work — `pointer-events-none`
    //    would strand the user unable to open the submenu once they're on
    //    the group's own listing.
    if (isUrlActiveLeaf && !hasChildren) className += ROW_DISABLED_CLASS;
  }
  // Parent-highlighted (collapsed parent with an active descendant) OR
  // expanded parent — both states get the green text + (for depth 0 only
  // — "Sales orders") the green left bar. Design Image #9 shows "Sales
  // orders" with the bar visible even when its tree is expanded, so the
  // previous `&& !isExpanded` gate is dropped. A NESTED parent (depth 1
  // — Amazon, Baldor, GFS, Sysco, …) deliberately stays lighter-weight:
  // green text + the depth-1 dot (`isDotVisible`) is enough; product
  // feedback was explicit that the bar + background-fill treatment
  // should stay reserved for the top-level group header only, even
  // though Vue's own `nav.scss` applies it at both levels.
  if (isParentHighlighted) {
    className += ROW_PARENT_ACTIVE_GENERIC;
    if (depth === 0) className += ROW_ACTIVE_TOPLEVEL_EXTRA;
  }
  return className;
}

// Compose the inline style for a panel row's active state. Extracted from
// `renderPanelItem` to reduce its cognitive complexity (S3776).
//
// Inline style is the FOOLPROOF way to paint the active brand-green colour —
// the Tailwind `[color:var(--panel-active)]` arbitrary utility doesn't always
// survive the CRA/Tailwind compile when `text-[var(--panel-text)]` is already
// on the row. Inline `style={}` wins via highest specificity and uses native
// `var()` resolution.
//
// For depth-0 active rows the left bar is drawn via `box-shadow: inset` (NOT
// `border-left` or an absolutely-positioned `<span>`) — both earlier
// strategies failed for depth-0 leaves (Accounting → "AR Contacts").
//
// The BACKGROUND fill (`--panel-active-bg`) is Vue parity that was never
// actually wired up: `nav.scss`'s `.router-link-collapse .n-collapse-item__
// header-main { background: var(--hk-date-range-background-color); border-
// left: 2px solid ...; border-radius: 2px; }` paints a tinted background +
// border-left + rounded corners on a top-level collapsible item the moment
// `isChildActive` is true (Vue's own name for this file's `isParentActive`)
// — e.g. "Sales orders" when "Baldor" is the active descendant. `--panel-
// active-bg` (`#212121` dark / `#ecfdf5` light) is this file's own existing
// token for exactly that colour — already documented at the top of this
// file as "Active parent bg" — but until now it was only ever wired to a
// plain `:hover` rule (`PANEL_COLLAPSE_CLASS`'s row), never to the actual
// active state this comment always claimed it was for.
//
// Depth-0 ONLY, by explicit product feedback: Vue's own `.router-link-
// collapse` rule actually applies this same background+border treatment
// at the NESTED (depth-1 — Amazon, Baldor, GFS, Sysco, …) level too, but
// that was tried here and rejected — a nested parent containing an
// active descendant should stay lighter-weight (green text + the
// depth-1 dot is enough), with the background/bar reserved for the
// top-level group header.
function computePanelRowInlineStyle(args: {
  depth: number;
  isActiveLeaf: boolean;
  isParentHighlighted: boolean;
}): React.CSSProperties | undefined {
  const { depth, isActiveLeaf, isParentHighlighted } = args;
  if (!isActiveLeaf && !isParentHighlighted) {
    return undefined;
  }
  const style: React.CSSProperties = { color: "var(--panel-active)" };
  if (depth === 0) {
    style.boxShadow = "inset 2px 0 0 var(--panel-active)";
    style.backgroundColor = "var(--panel-active-bg)";
    style.borderRadius = "2px";
  }
  return style;
}

// Render the expand/collapse chevron for a panel item with children.
// The icon's stroke uses `currentColor` (PANEL_CHEVRON_CLASS sets
// `text-current`), so it automatically picks up whatever colour the
// parent button is using — no separate active-style needed here.
function renderPanelChevron(isExpanded: boolean): React.ReactElement {
  if (isExpanded) {
    return (
      <IconChevronDown size={CHEVRON_SIZE_PX} className={PANEL_CHEVRON_CLASS} />
    );
  }
  return (
    <IconChevronRight size={CHEVRON_SIZE_PX} className={PANEL_CHEVRON_CLASS} />
  );
}
const PANEL_LABEL_CLASS =
  "flex-1 whitespace-nowrap overflow-hidden text-ellipsis";

// Active-leaf bullet dot — sits CENTERED on the outer vertical-guide
// line. Geometry:
//   - SUBLIST_OUTER's `::before` vertical line is at sublist_x = 28px
//     (sublist `pl-6` = 24px + `before:left-7` = 28px → line at 28px
//     from sublist left edge, which is also panel + 28px).
//   - Depth-1 row's left edge sits at sublist content edge = panel + 24px.
//   - Dot is 6px wide → its left edge must be at panel + 25px to
//     centre on the line (25..31, midpoint 28) → relative to the row
//     that's `left:1px`.
// `[background-color:var(--panel-active)]` (explicit property) instead
// of `bg-[var(--panel-active)]` to bypass Tailwind's CSS-variable
// shorthand ambiguity.
const PANEL_DOT_CLASS =
  "w-[6px] h-[6px] rounded-full [background-color:var(--panel-active)] shrink-0" +
  " absolute left-[-1px] top-1/2 -translate-y-1/2";

const PANEL_EMPTY_CLASS =
  "p-[14px] m-0 text-[var(--panel-empty-text)] text-[13px]";

// Loading skeleton (mirrors Vue's n-skeleton during permission fetch).
// Tailwind doesn't auto-register the shimmer keyframes we need here, so
// we set the gradient + background-size via arbitrary values and use the
// `animate-shimmer` utility already in `tailwind.config.js`.
const SKELETON_RAIL_CLASS =
  "h-14 mx-3 my-2 rounded animate-shimmer bg-[length:200%_100%]" +
  " bg-[linear-gradient(90deg,rgba(255,255,255,0.04)_0%,rgba(255,255,255,0.12)_50%,rgba(255,255,255,0.04)_100%)]";

/**
 * Return the IDs of every direct sibling of `target` (including target itself)
 * by walking the menu tree. Used to enforce accordion behavior — opening one
 * expandable item collapses any other open sibling.
 */
const findSiblingIds = (
  items: ReadonlyArray<MenuItem>,
  target: string,
): string[] | null => {
  for (const it of items) {
    if (it.children?.some((c) => c.id === target)) {
      return it.children.map((c) => c.id);
    }
    if (it.children) {
      const found = findSiblingIds(it.children, target);
      if (found) return found;
    }
  }
  // No parent found → target is at the top level.
  if (items.some((c) => c.id === target)) {
    return items.map((c) => c.id);
  }
  return null;
};

interface AsideMenuProps {
  /**
   * Mobile drawer open state. Ignored at >=769px where the aside is a
   * static, always-visible sidebar. At <=768px `false` keeps the drawer
   * slid off-screen; `true` slides it in. Defaults to `false`.
   */
  open?: boolean;
  /** Close the mobile drawer (backdrop tap, menu selection, etc.). */
  onClose?: () => void;
}

const AsideMenu: React.FC<AsideMenuProps> = ({ open = false, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const rootClassName =
    ROOT_CLASS_BASE + (open ? ROOT_CLASS_OPEN : ROOT_CLASS_CLOSED);
  const { logout } = useAuth();
  const { setBreadcrumbs } = useBreadcrumbs();
  // Status alone — the filtering itself lives in `useFilteredMenu`.
  const { status: permissionStatus } = usePermissions();
  /**
   * Filtered menu — the shared canonical hook (also feeds BottomTabBar
   * and MobileMenuSheet, so every menu surface agrees). Recomputed only
   * when permissions land or change.
   */
  const filteredMenu = useFilteredMenu();

  const {
    activeRailId: derivedRailId,
    isLeafActive,
    isParentActive,
  } = useActiveMenu(filteredMenu);

  const [userSelectedRailId, setUserSelectedRailId] = useState<string | null>(
    null,
  );
  // `activeRailId` decides which rail's PANEL is shown — it's fine for a
  // user click to override this so someone can browse another section's
  // menu without leaving the page they're on.
  const activeRailId =
    userSelectedRailId ?? derivedRailId ?? filteredMenu[0]?.id ?? null;
  // `trueActiveRailId` decides which rail ICON is marked active — this must
  // NEVER follow `userSelectedRailId`. A click on a rail whose leaves are
  // all Vue-owned (e.g. Returns — every leaf opens a new tab) never changes
  // `location.pathname`, so `derivedRailId` never changes either, and the
  // effect below (which only resets `userSelectedRailId` on navigation)
  // never fires: the clicked rail stayed lit green INDEFINITELY, masking
  // the page you're actually on (e.g. Settings → API keys), until you
  // happened to navigate somewhere whose derived rail genuinely differed.
  // Splitting "which panel is open" from "which page am I on" fixes this
  // without touching the browse-another-section affordance.
  const trueActiveRailId = derivedRailId ?? filteredMenu[0]?.id ?? null;

  // Menu clicks open the destination in a NEW TAB (`handleLeafSelect`
  // calls `window.open`), so the React tab's URL never changes and
  // `useActiveMenu` (which derives active state from `useLocation`)
  // never lights up the clicked item. To still show the user which
  // leaf they last clicked we keep an in-memory `clickedLeafId` and
  // OR it into the active-state checks below. This is purely visual
  // — refreshing the page will fall back to URL-based detection.
  const [clickedLeafId, setClickedLeafId] = useState<string | null>(null);

  // Set of menu-item IDs that are ancestors of `clickedLeafId` — used
  // so the clicked leaf's parent chain (CW → Sales orders) also lights
  // up. Walks the filtered menu tree once whenever the click changes.
  const clickedAncestorIds = useMemo<ReadonlySet<string>>(() => {
    if (!clickedLeafId) return new Set();
    const acc = new Set<string>();
    const walk = (
      items: ReadonlyArray<MenuItem>,
      ancestors: string[],
    ): boolean => {
      for (const it of items) {
        if (it.id === clickedLeafId) {
          ancestors.forEach((a) => acc.add(a));
          return true;
        }
        if (it.children && walk(it.children, [...ancestors, it.id]))
          return true;
      }
      return false;
    };
    walk(filteredMenu, []);
    return acc;
  }, [clickedLeafId, filteredMenu]);

  // Panel always starts CLOSED on page load. See pre-migration comment for
  // history.
  const [panelOpen, setPanelOpen] = useState<boolean>(false);
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  // Clears the browse override on any REAL navigation (a leaf click, a
  // breadcrumb, browser back/forward) so a page change always hands the
  // panel back to URL-derived truth, matching `trueActiveRailId` above.
  // Keyed on `location.pathname`, not `derivedRailId`: two different pages
  // can derive the SAME rail id, which a `[derivedRailId]` dependency would
  // never notice as a "change" worth clearing the override for.
  useEffect(() => {
    setUserSelectedRailId(null);
  }, [location.pathname]);

  // When the mobile drawer opens, auto-expand the panel for the currently
  // active rail section so the user lands on their open submenu (e.g.
  // Purchasing → Purchase Orders / Containers / Vendors) instead of a
  // bare rail. Mirrors Vue ERP's mobile drawer, which reveals the active
  // section's submenu on open. No-op on desktop, where `open` stays false
  // and the panel keeps its own open/collapse state.
  useEffect(() => {
    if (open) setPanelOpen(true);
  }, [open]);

  // Deactivate the transient click-highlight on real in-tab navigation.
  // `clickedLeafId` is set on every menu click so a clicked leaf lights up
  // even when it opens in a NEW TAB (Vue routes don't change this tab's
  // URL). But it was never cleared, so navigating IN this tab without a
  // menu click — browser Back, a breadcrumb, or moving between the two
  // React pages (/ar-contacts <-> /shipping-prices) — left the previously
  // clicked leaf stuck green alongside the new URL-derived active leaf.
  // Clearing it on pathname change hands control back to URL-based
  // detection. New-TAB (Vue) clicks don't change `pathname`, so their
  // highlight still survives — exactly as intended.
  useEffect(() => {
    setClickedLeafId(null);
  }, [location.pathname]);

  const toggleExpand = useCallback(
    (id: string): void => {
      setExpandedIds((prev) => {
        const wasExpanded = prev.has(id);
        const siblings = findSiblingIds(filteredMenu, id) ?? [id];
        const next = new Set(prev);
        // Accordion: close every sibling at this level, including the clicked
        // item if it was already open. If it was closed, re-add it so it opens.
        siblings.forEach((sibId) => next.delete(sibId));
        if (!wasExpanded) next.add(id);
        return next;
      });
    },
    [filteredMenu],
  );

  const setBreadcrumbsForLeaf = useCallback(
    (path: ReadonlyArray<MenuItem>): void => {
      const trail: BreadcrumbItem[] = path.map((item) => ({
        label: t(item.labelKey, { defaultValue: item.label }),
        path: item.route ?? "",
        isReactRoute: item.isReactRoute ?? false,
      }));
      setBreadcrumbs(trail);
    },
    [setBreadcrumbs, t],
  );

  const handleLeafSelect = useCallback(
    (path: ReadonlyArray<MenuItem>): void => {
      const leaf = path[path.length - 1];
      if (!leaf?.route) return;
      // A Vue destination ALWAYS opens in a new tab, even on a plain click, so
      // the React app is never torn down by a cross-origin navigation — the
      // user keeps this tab (and any in-flight work on it) and comes back to it
      // by closing the new one.
      //
      // It shares the early return above for the same reason Ctrl-click does:
      // this tab does not navigate, so it must NOT set clickedLeafId or swap
      // the breadcrumb, which would highlight a destination this tab never
      // went to. The drawer still closes — on mobile it would otherwise sit
      // open over the page the user is left looking at.
      if (!leaf.isReactRoute) {
        navigateToVueRoute({ vuePath: leaf.route, openInNewTab: true });
        onClose?.();
        return;
      }
      // Remember this click for the in-memory highlight fallback used
      // by `clickedAncestorIds` (purely visual — main active state is
      // still driven by URL match in `useActiveMenu`).
      setClickedLeafId(leaf.id);
      setBreadcrumbsForLeaf(path);
      // Close the mobile drawer on selection so it doesn't linger over the
      // destination page. No-op on desktop (drawer state is unused there).
      onClose?.();
      // React routes only by this point — Vue destinations returned above.
      navigate(leaf.route);
    },
    [navigate, setBreadcrumbsForLeaf, onClose],
  );

  const handleRailClick = useCallback((id: string): void => {
    setUserSelectedRailId(id);
    setPanelOpen(true);
  }, []);

  const togglePanel = useCallback((): void => {
    setPanelOpen((prev) => !prev);
  }, []);

  const activeRail = useMemo(
    () => filteredMenu.find((m) => m.id === activeRailId) ?? null,
    [activeRailId, filteredMenu],
  );

  const renderPanelItem = (
    item: MenuItem,
    parents: ReadonlyArray<MenuItem>,
    depth: number,
  ): React.ReactNode => {
    const hasChildren = !!item.children?.length;
    const isExpanded = expandedIds.has(item.id);
    // Genuine URL match, straight from the hook — no click-memory OR'd in.
    // Used to gate the "disable re-navigation" styling: only a real match
    // means the user is actually on this page.
    const isUrlActiveLeaf = isLeafActive(item);
    // OR-in the in-memory click state with the URL-derived state so
    // new-tab navigations still light up the clicked leaf + its parent
    // chain in THIS tab.
    const isActiveLeaf = isUrlActiveLeaf || item.id === clickedLeafId;
    const isParentHighlighted =
      hasChildren && (isParentActive(item) || clickedAncestorIds.has(item.id));
    /*
      Green bullet dot — ROUTE-driven. Per the design (Images #9, #11,
      #12): the dot appears ONLY on depth-1 rows (top-level menu's
      direct children — "All orders", "CW", etc.), never on the
      depth-2+ grandchildren (CW Direct / Dropship / Stock).
      Conditions for showing:
      - depth === 1 AND
      - either the row IS the active leaf ("All orders"), OR
      - the row is a parent that contains the active descendant
        ("CW" when CW Dropship is active) — regardless of whether
        the parent is currently expanded (matches Image #12 where
        a collapsed CW still carries the dot).
    */
    const isDotVisible =
      depth === 1 && (isActiveLeaf || (hasChildren && isParentHighlighted));
    const ItemIcon = item.icon;

    const handleClick = (): void => {
      if (hasChildren) {
        toggleExpand(item.id);
        // Vue parity: a collapsible group whose header is itself a
        // `router-link` (AsideMenu.vue's `collapsable` + `routeLink` combo —
        // e.g. "Sales orders" -> /orders/sales-orders/all, "Amazon" ->
        // /orders/sales-orders/amazon) ALSO navigates on click; expanding and
        // navigating are independent, not mutually exclusive. A group's own
        // `route` wins when set (e.g. "Amazon", a real parent-group page);
        // otherwise fall back to `landingRoute` (e.g. "Sales orders", which
        // has no page of its own and lands on its All-orders leaf). Groups
        // with neither (e.g. "Settings") just toggle, as before.
        if (item.route && item.isReactRoute) {
          handleLeafSelect([...parents, item]);
        } else if (item.landingRoute) {
          handleLeafSelect([
            ...parents,
            { ...item, route: item.landingRoute, isReactRoute: true },
          ]);
        }
        return;
      }
      handleLeafSelect([...parents, item]);
    };

    // Compose row class. The visual hierarchy:
    //   1. ROW_BASE — every row gets the base flex / padding / hover rules
    //      (this also serves as the PARENT class for depth 0).
    //   2. depth 1 → ROW_CHILD_CLASS — no icon, no border, indented so
    //      the label aligns with the parent's label x-position.
    //   3. depth ≥ 2 → ROW_SUBCHILD_CLASS + ROW_SUBCHILD_CONNECTOR — same
    //      typography as child, tighter pl, plus a horizontal dash from
    //      the vertical guide line to the row.
    //   4. Active leaf — green text/icon at every depth. PLUS at depth 0,
    //      add the panel-active bg + green left bar. PLUS at depth ≥ 2,
    //      the connector dash turns green.
    //   5. Parent-active (collapsed parent containing the active route) —
    //      same treatment as active leaf at the matching depth.
    //   6. Disabled flag — pointer-events:none on the active leaf so the
    //      user can't re-navigate to the page they're on.
    const className = computePanelRowClassName({
      depth,
      isActiveLeaf,
      isUrlActiveLeaf,
      isParentHighlighted,
      hasChildren,
    });

    // See `computePanelRowInlineStyle` for the rationale behind painting
    // the active state via inline style (highest specificity, beats the
    // Tailwind compile-order races) and the depth-0 left bar via an
    // inset box-shadow.
    const inlineActiveStyle = computePanelRowInlineStyle({
      depth,
      isActiveLeaf,
      isParentHighlighted,
    });

    return (
      <li key={item.id} className={PANEL_ITEM_CLASS}>
        <button
          type="button"
          className={className}
          style={inlineActiveStyle}
          aria-expanded={hasChildren ? isExpanded : undefined}
          onClick={handleClick}>
          {isDotVisible ? (
            <span
              className={PANEL_DOT_CLASS}
              style={{ backgroundColor: "var(--panel-active)" }}
              aria-hidden
            />
          ) : null}
          {ItemIcon && depth === 0 ? (
            <ItemIcon size={PANEL_ICON_SIZE_PX} className={PANEL_ICON_CLASS} />
          ) : null}
          <span className={PANEL_LABEL_CLASS}>
            {t(item.labelKey, { defaultValue: item.label })}
          </span>
          {hasChildren ? renderPanelChevron(isExpanded) : null}
        </button>

        {hasChildren ? (
          <div
            className={
              SUBLIST_WRAPPER_BASE + (isExpanded ? SUBLIST_WRAPPER_OPEN : "")
            }
            aria-hidden={!isExpanded}>
            <ul
              className={
                depth === 0 ? SUBLIST_OUTER_CLASS : SUBLIST_NESTED_CLASS
              }>
              {item.children!.map((child) =>
                renderPanelItem(child, [...parents, item], depth + 1),
              )}
            </ul>
          </div>
        ) : null}
      </li>
    );
  };

  const handleLogout = (): void => {
    void logout();
  };

  // Skeleton while permissions are still being fetched. Mirrors Vue's
  // `<n-skeleton>` block shown until isPermissionLoaded.
  if (permissionStatus === "idle" || permissionStatus === "loading") {
    return (
      <>
        <style>{ASIDE_MENU_THEME_STYLE}</style>
        <div className={rootClassName} aria-busy="true">
          <div className={RAIL_CLASS}>
            <div className={RAIL_LOGO_CLASS} aria-hidden />
            <div className={SKELETON_RAIL_CLASS} />
            <div className={SKELETON_RAIL_CLASS} />
            <div className={SKELETON_RAIL_CLASS} />
            <div className={SKELETON_RAIL_CLASS} />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{ASIDE_MENU_THEME_STYLE}</style>
      <div className={rootClassName}>
        {/*
          Floating Submit Ticket pill — sits at the bottom-left of the
          viewport, overlaying the aside menu. Mirrors Vue's
          <ReportBug isFloatButton /> rendered alongside AsideMenu.vue.
        */}
        <ReportBug floating />

        {/* Left rail */}
        <div className={RAIL_CLASS}>
          <div className={RAIL_LOGO_CLASS} aria-hidden>
            <img
              src={`${PUBLIC_ASSET_BASE}/RWLogoNew.svg`}
              alt=""
              className={LOGO_IMG_CLASS}
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
            />
          </div>

          <nav aria-label="Primary" className={RAIL_NAV_CLASS}>
            <ul className={RAIL_LIST_CLASS}>
              {filteredMenu.map((item) => {
                const isActive = trueActiveRailId === item.id;
                const ItemIcon = item.icon;
                return (
                  <li key={item.id} className={RAIL_ITEM_CLASS}>
                    <button
                      type="button"
                      className={
                        RAIL_BTN_BASE + (isActive ? RAIL_BTN_ACTIVE : "")
                      }
                      aria-current={isActive ? "page" : undefined}
                      onClick={() => handleRailClick(item.id)}>
                      {ItemIcon ? (
                        <ItemIcon
                          size={RAIL_ICON_SIZE_PX}
                          className={RAIL_ICON_CLASS}
                        />
                      ) : null}
                      <span className={RAIL_LABEL_CLASS}>
                        {t(item.labelKey, { defaultValue: item.label })}
                      </span>
                    </button>
                  </li>
                );
              })}
              <li className={RAIL_ITEM_CLASS}>
                <button
                  type="button"
                  className={RAIL_BTN_BASE + RAIL_BTN_LOGOUT_EXTRA}
                  onClick={handleLogout}>
                  <span className={RAIL_LABEL_CLASS}>{t("menu.logout")}</span>
                </button>
              </li>
            </ul>
          </nav>
        </div>

        {/* Right panel */}
        {panelOpen ? (
          <aside
            className={PANEL_CLASS}
            aria-label={
              activeRail
                ? t(activeRail.labelKey, { defaultValue: activeRail.label })
                : undefined
            }>
            <div className={PANEL_TOPBAR_CLASS}>
              <button
                type="button"
                className={PANEL_COLLAPSE_CLASS}
                aria-label={t("menu.collapse")}
                onClick={togglePanel}>
                <IconDoubleChevronLeft size={18} />
              </button>
            </div>

            {activeRail?.children?.length ? (
              <nav>
                <ul className={PANEL_LIST_CLASS}>
                  {activeRail.children.map((child) =>
                    renderPanelItem(child, [activeRail], 0),
                  )}
                </ul>
              </nav>
            ) : (
              <p className={PANEL_EMPTY_CLASS}>{t("menu.empty")}</p>
            )}
          </aside>
        ) : null}
      </div>
    </>
  );
};

export default AsideMenu;
